
import CmSvgIcon from '@/components/cm-svg-icon/cm-svg-icon.vue'


import { computed, defineComponent, h, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import LoadMoreButton from '@/components/load-more-button.vue'
import ContentAdaptLayout from '../../_components/content-adapt-layout/content-adapt-layout.vue'
import DetailDrawer from './_component/detail-drawer/detail-drawer.vue'

import { TaskItem, Message, MessagePushService } from './message-push.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { formatTime } from '@/utils/time'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import Keywords from '../../data-safe/anti-external-control/components/Keywords.vue'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import { FormType, ObjectFormItem, DateType } from '@/components/cmForm/types'
import { useParseTime } from '@/hooks'
import dayjs from 'dayjs'

export default defineComponent({
  props: {
    isTerminalInfo: {
      type: Boolean,
      default: false
    },
    deviceId: {
      type: String,
      default: ''
    }
  },
  components: {
    SearchFilterBar,
    LoadMoreButton,
    ContentAdaptLayout,
    DetailDrawer,Keywords,
    ExportDropdown,
    RawDataDrawer
  },
  setup (props, context) {
    const { t, te, locale } = useI18n()
    const injector = useReflectiveInjector([MessagePushService])
    const service = injector.get(MessagePushService)
     function parseArrayOnly(str) {
      try {
        const parsed = JSON.parse(str)
        return Array.isArray(parsed) ? parsed : null
      } catch (e) {
        return null
      }
    }
    const data = reactive({
      loading: false,
      total: 0,
      list: [] as Message[],
    })

    const hasMore = ref(true)
    const defaultDate = props.isTerminalInfo
      ? undefined
      : [dayjs().subtract(7, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] as [string, string]
    const [defaultStartTime, defaultEndTime] = useParseTime(defaultDate || [])

    const queryParams = reactive<any>({
      page: 1,
      limit: 20,
      task_type: undefined,
      dateRange: defaultDate,
      start_time: defaultStartTime,
      end_time: defaultEndTime,
      device_id: props.deviceId,
    })

    const taskNameList = ref<TaskItem[]>([])
    const taskNameMap = reactive<Record<number, string>>({})

    function resolveTaskTypeLabel (taskType: number, apiFallback: string): string {
      const key = `noticeManage.taskType.${taskType}`
      return te(key) ? t(key) : apiFallback
    }

    function rebuildTaskNameMap (): void {
      const fresh: Record<number, string> = {}
      for (const item of taskNameList.value) {
        fresh[item.task_type] = resolveTaskTypeLabel(item.task_type, item.name)
      }
      for (const k of Object.keys(taskNameMap)) {
        delete taskNameMap[Number(k)]
      }
      Object.assign(taskNameMap, fresh)
    }
    const isShowRawData = ref(false)
    const currentRow = ref<any>({})

    const filterFormColumns = computed<Partial<ObjectFormItem<any, any>>>(() => {
      void locale.value
      return {
        task_type: {
          colOpt: { style: 'flex: 0 0 130px' },
          label: t('noticeManage.taskName'),
          type: FormType.Select,
          outSearchFiledFlag: true,
          selectOpt: { clearable: true },
          dictData: taskNameList.value.map(item => ({
            label: resolveTaskTypeLabel(item.task_type, item.name),
            value: item.task_type,
          })),
          events: {
            change: () => { request() },
          },
        },
        dateRange: {
          colOpt: { style: 'flex: 0 0 280px' },
          type: FormType.Date,
          dateType: DateType.DateRange,
          outSearchFiledFlag: true,
          events: {
            change: (val: any) => {
              if (val) {
                const [start, end] = useParseTime(val)
                queryParams.start_time = start
                queryParams.end_time = end
              } else {
                queryParams.start_time = undefined
                queryParams.end_time = undefined
              }
              request()
            },
          },
          dateOpt: {
            'value-format': 'YYYY-MM-DD',
            'start-placeholder': t('common.startDate'),
            'end-placeholder': t('common.endDate'),
          },
        },
      }
    })

    // 翻页表格
    function request (isLoadMore: boolean = false) {
      if (!isLoadMore) {
        queryParams.page = 1
        data.list = []
      }
      data.loading = true
      service.getMessageList(queryParams as any, props.isTerminalInfo).then((res: any) => {
        if (queryParams.page === 1) {
          data.list = res.list || []
        } else {
          data.list = [...data.list, ...(res.list || [])]
        }
        data.total = res.total || 0
        hasMore.value = data.list.length < data.total
      }).finally(() => {
        data.loading = false
      })
    }

    request()

    const state = reactive({
      detailDrawser: false,
      detailTypeName: '',
      row: {} as Message
    })

    function handleQuery (form: any) {
      if (form.dateRange) {
        const [start, end] = useParseTime(form.dateRange)
        form.start_time = start
        form.end_time = end
      } else {
        form.start_time = undefined
        form.end_time = undefined
      }
      Object.assign(queryParams, form)
      queryParams.task_type = queryParams.task_type || -1
      queryParams.page = 1
      request()
    }

    function handleReset () {
      queryParams.task_type = -1
      queryParams.dateRange = defaultDate
      const [start, end] = useParseTime(defaultDate || [])
      queryParams.start_time = start
      queryParams.end_time = end
      queryParams.task_type = -1
      queryParams.page = 1
      request()
    }

    function loadMoreData () {
      if (!hasMore.value || data.loading) return
      queryParams.page = queryParams.page! + 1
      request(true)
    }

    // 表格 - 下发状态栏 - 点击事件
    function handleColumn (
      action: 'finish' | 'detail' | 'cancel',
      row: Message
    ) {
      switch (action) {
        case 'finish':
        case 'detail':
          state.row = row
          state.detailTypeName = taskNameMap[state.row.task_type]
          state.detailDrawser = true
          break
        case 'cancel':
          ElMessageBox({
            title: t('cmtable.tip'),
            showCancelButton: true,
            message: h('div', { style: 'display:flex;align-items:center;margin-left:10px;margin-top:25px;margin-bottom:20px' }, [
              h(CmSvgIcon, { name: 'views/dashboard/notice-manage/message-push/_images/client_modal_tip', size: 20, monochrome: true }),
              h('div', { style: 'margin-left:10px' }, [
                h('p', null, t('noticeManage.cancelPushConfirm')),
              ])
            ]),
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel'),
          }).then(() => {
            return service.cancel({
              task_id: row.task_id
            })
          }).then(() => {
            ElMessage({
              showClose: true,
              message: t('noticeManage.cancelPushSuccess'),
              type: 'success'
            })
            request()
          }).catch(() => {
            request()
          })
      }
    }

    function handleEvent (
      action: 'time' | 'select' | 'export' | 'updata-count',
      data?: any
    ) {
      switch (action) {
        case 'time':
          // Handled by dateRange change event
          break
        case 'select':
          // Handled by handleQuery
          break
        case 'export':
          break
        case 'updata-count': // 更新下发状态数字
          data.row[data.key] = data.count
          break
      }
    }

    function getTaskName () {
      service.getTaskType().then(response => {
        taskNameList.value = response.list
        rebuildTaskNameMap()
      })
    }

    getTaskName()

    watch(locale, () => {
      if (taskNameList.value.length) {
        rebuildTaskNameMap()
      }
    })

    return {
      state,
      data,
      queryParams,
      filterFormColumns,
      hasMore,
      parseArrayOnly,
      isShowRawData,
      currentRow,
      showRawData (row: any) {
        currentRow.value = row
        isShowRawData.value = true
      },
      handleColumn,
      formatTime,
      handleEvent,
      taskNameMap,
      handleQuery,
      handleReset,
      loadMoreData,
      download (type: 0|1) {
        service.download(type, queryParams as any, t('noticeManage.exportFileMessageCommand'))
      },
    }
  }
})
